import React, { useEffect, useMemo, useState } from "react";
import { X, MapPin } from "lucide-react";
import { useGame } from "../game/em2c-store";

function depositLabel(deposit) {
  if (!deposit) return "None";
  if (deposit.kind === "hydrated_minerals" || deposit.kind === "water") return "Hydrated Mineral Zone";
  if (deposit.kind === "metal") return "Metal Deposit";
  return (deposit.label || deposit.kind || "Deposit")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function HexInfoPanel() {
  const [selected, setSelected] = useState(null);
  const grid = useGame((s) => s.grid);
  const deposits = useGame((s) => s.deposits || []);

  useEffect(() => {
    const onHex = (event) => {
      const next = event.detail || null;
      setSelected((current) => {
        if (!next) return null;
        // Terrain inspection is an intentional two-state interaction:
        // first terrain click opens the inspected hex; the next terrain click
        // closes the panel. This is more reliable than requiring the pointer
        // to resolve to the exact same axial cell on the second click.
        if (current) return null;
        return next;
      });
    };

    window.addEventListener("mars:hex-selected", onHex);
    return () => window.removeEventListener("mars:hex-selected", onHex);
  }, []);

  const info = useMemo(() => {
    if (!selected) return null;
    const hex = grid?.get?.(`${selected.q},${selected.r}`) || null;
    const deposit = hex?.depositId
      ? deposits.find((d) => d.id === hex.depositId)
      : deposits.find((d) =>
          (d.hexes || []).some((h) => h.q === selected.q && h.r === selected.r)
        );
    return { hex, deposit };
  }, [selected, grid, deposits]);

  if (!selected) return null;

  const hex = info?.hex;
  const deposit = info?.deposit;
  const remaining = deposit ? Math.max(0, Number(deposit.remaining || 0)) : 0;
  const capacity = deposit ? Math.max(0, Number(deposit.capacity || 0)) : 0;

  return (
    <div
      className="absolute right-3 bottom-16 z-[45] pointer-events-auto w-64 bg-[#0A0C10]/90 backdrop-blur-md border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
      style={{ fontFamily: "Rajdhani, sans-serif" }}
      data-testid="hex-info-panel"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <MapPin size={13} className="text-cyan-400" />
        <div className="flex-1">
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Terrain</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-white">
            Hex {selected.q}, {selected.r}
          </div>
        </div>
        <button
          onClick={() => setSelected(null)}
          className="text-slate-500 hover:text-white"
          aria-label="Close terrain info"
        >
          <X size={13} />
        </button>
      </div>

      <div className="p-3 space-y-2 text-[11px] font-mono">
        <div className="flex justify-between gap-3">
          <span className="text-slate-500 uppercase">Surface</span>
          <span className="text-slate-200">Martian Regolith</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-slate-500 uppercase">Deposit</span>
          <span className={deposit ? "text-cyan-400" : "text-slate-300"}>{depositLabel(deposit)}</span>
        </div>
        {deposit && (
          <>
            <div className="flex justify-between gap-3">
              <span className="text-slate-500 uppercase">Survey</span>
              <span className={deposit.discovered ? "text-[#00fa9a]" : "text-[#ffc107]"}>
                {deposit.discovered ? "Discovered" : "Unsurveyed"}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-slate-500 uppercase">Remaining</span>
              <span className="text-white">{Math.floor(remaining)} / {Math.floor(capacity)}</span>
            </div>
          </>
        )}
        <div className="flex justify-between gap-3">
          <span className="text-slate-500 uppercase">Buildable</span>
          <span className={hex?.buildable ? "text-[#00fa9a]" : "text-[#ff8c00]"}>
            {hex?.buildable ? "Yes" : "No"}
          </span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-slate-500 uppercase">Occupied</span>
          <span className="text-slate-200">{hex?.occupied ? "Yes" : "None"}</span>
        </div>
      </div>
    </div>
  );
}
